import {FlowLang} from 'flow-to-typescript-codemod';
import * as React from 'react';
import {type Expression} from '@packages/systems/wfdl-ast';
import {TypeImplementation, type Type} from '@packages/systems/wfdl';
import {type ContextualExpression} from '@packages/utilities/contextual-expression';
import type {
  ExpressionBindMetaPayload,
  ExpressionEditorDispatch,
} from './types';

export type ExpressionEditorImplementationProps = {
  expression: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
};

type ExpressionEditorImplementationContextDependencies = {
  env: TypeImplementation.DataTypeEnvironment;
};

export type ExpressionBindingImplementationProps = {
  expression: ContextualExpression;
  children?: React.ReactNode;
  onChange: (
    expression: null | Expression,
    meta: ExpressionBindMetaPayload
  ) => void;

  button?: (
    arg1: (arg1: unknown) => void,
    targetRef: React.RefObject<HTMLElement>
  ) => React.ReactElement<any>;
  bindUIVariant: 'dot' | 'custom' | 'label';
  automationLabel?: string;
  style?: React.CSSProperties;
  uiFlags?: TypeImplementation.DataTypeEnvironment['uiFlags'];
  hasArguments?: boolean;
};

export type TypeEditorProps = {
  type: undefined | Type;
  onChange: (arg1: Type) => any;
};

// @ts-expect-error - TS2322 - Type 'Context<{ ExpressionEditor: () => never; ExpressionBinding: () => never; TypeEditor: () => never; }>' is not assignable to type 'Context<{ ExpressionEditor: AbstractComponent<ExpressionEditorImplementationProps, any>; ExpressionBinding: AbstractComponent<ExpressionBindingImplementationProps, any>; TypeEditor: AbstractComponent<...>; }>'.
export const ExpressionEditorContext: React.Context<{
  ExpressionEditor: FlowLang.AbstractComponent<ExpressionEditorImplementationProps>;
  ExpressionBinding: FlowLang.AbstractComponent<ExpressionBindingImplementationProps>;
  TypeEditor: FlowLang.AbstractComponent<TypeEditorProps>;
}> = React.createContext({
  ExpressionEditor: () => {
    throw new TypeError(
      'You must wrap this area with <ExpressionEditorImplementationContext.Provider>'
    );
  },
  ExpressionBinding: () => {
    throw new TypeError(
      'You must wrap this area with <ExpressionEditorImplementationContext.Provider>'
    );
  },
  TypeEditor: () => {
    throw new TypeError(
      'You must wrap this area with <ExpressionEditorImplementationContext.Provider>'
    );
  },
});

const defaultExpressionEditorImplementationValue: any = new Proxy(
  {},
  // @ts-expect-error - TS2560 - Value of type '() => never' has no properties in common with type 'ProxyHandler<object>'. Did you mean to call it?
  () => {
    throw new Error(
      'Use ExpressionEditorImplementationContext.Provider wrapping your ExpressionEditor'
    );
  }
);

export const ExpressionEditorImplementationContext =
  React.createContext<ExpressionEditorImplementationContextDependencies>(
    defaultExpressionEditorImplementationValue
  );

export const useExpressionEditorImplementationContext = () =>
  React.useContext(ExpressionEditorImplementationContext);

export const useExpressionEditorContext = () =>
  React.useContext(ExpressionEditorImplementationContext).env.context;

export const ExpressionEditor = (
  props: ExpressionEditorImplementationProps
) => {
  const {ExpressionEditor: Input} = React.useContext(ExpressionEditorContext);
  return <Input {...props} />;
};

export const ExpressionBinding = (
  props: ExpressionBindingImplementationProps
) => {
  const {ExpressionBinding: Input} = React.useContext(ExpressionEditorContext);
  return <Input {...props} />;
};

export const TypeEditor = (props: TypeEditorProps) => {
  const {TypeEditor: Input} = React.useContext(ExpressionEditorContext);
  return <Input {...props} />;
};
